import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { MultiSelect } from 'primereact/multiselect';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { toast } from 'react-toastify';
import { bidAnalysisServices } from 'services/bidAnalysis/bidAnalysisServices';
import styles from './RequestModificationModal.module.scss';

interface NavigationItem {
  id: string;
  label: string;
  icon: string;
  status?: 'completed' | 'warning' | 'pending';
}

interface RequestModificationModalProps {
  visible: boolean;
  onHide: () => void;
  navigationItems: NavigationItem[];
  bidId: number;
  onSuccess: () => void;
  bidStatus?: string;
}

const RequestModificationModal: React.FC<RequestModificationModalProps> = ({
  visible,
  onHide,
  navigationItems,
  bidId,
  onSuccess,
  bidStatus,
}) => {
  const [selectedSections, setSelectedSections] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ sections?: string; notes?: string }>({});

  // Create section options from navigationItems
  const sectionOptions = navigationItems.map((item) => ({
    label: item.label,
    value: item.id,
  }));

  const handleCancel = () => {
    setSelectedSections([]);
    setNotes('');
    setErrors({});
    onHide();
  };

  const validate = (): boolean => {
    const newErrors: { sections?: string; notes?: string } = {};

    if (selectedSections.length === 0) {
      newErrors.sections = 'Please select at least one section';
    }

    if (!notes.trim()) {
      newErrors.notes = 'Notes are required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSendRequest = async () => {
    // Check if bid is already approved
    if (bidStatus?.toLowerCase() === 'approved') {
      toast.error('Cannot request modification for an already approved bid');
      return;
    }

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Build the note combining sections and user notes
      const sectionLabels = selectedSections
        .map((id) => {
          const item = navigationItems.find((item) => item.id === id);
          return item?.label || id;
        })
        .join(', ');

      const combinedNote = `The following sections require modification: ${sectionLabels}. Notes: ${notes.trim()}`;

      await bidAnalysisServices.markNeedsModification(bidId, combinedNote);

      toast.success('Modification request sent successfully');
      handleCancel();
      onSuccess();
    } catch (error: any) {
      console.error('Error sending modification request:', error);
      toast.error(error?.response?.data?.message || 'Failed to send modification request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      header="Request Modification"
      visible={visible}
      onHide={handleCancel}
      style={{ width: '50vw', maxWidth: '600px' }}
      breakpoints={{ '960px': '75vw', '641px': '90vw' }}
      className={styles.modal}
      closable={true}
      closeOnEscape={true}
    >
      <div className={styles.modalContent}>
        <div className={styles.formGroup}>
          <label htmlFor="sections" className={styles.label}>
            Sections <span className={styles.required}>*</span>
          </label>
          <MultiSelect
            id="sections"
            value={selectedSections}
            options={sectionOptions}
            onChange={(e) => {
              setSelectedSections(e.value || []);
              if (errors.sections) {
                setErrors({ ...errors, sections: undefined });
              }
            }}
            placeholder="Select sections that need modification"
            className={`${styles.multiSelect} ${errors.sections ? styles.error : ''}`}
            display="chip"
            maxSelectedLabels={3}
            selectedItemsLabel="{0} sections selected"
            showClear
          />
          {errors.sections && <div className={styles.errorMessage}>{errors.sections}</div>}
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="notes" className={styles.label}>
            Notes <span className={styles.required}>*</span>
          </label>
          <InputTextarea
            id="notes"
            value={notes}
            onChange={(e) => {
              setNotes(e.target.value);
              if (errors.notes) {
                setErrors({ ...errors, notes: undefined });
              }
            }}
            placeholder="Describe what modifications are required..."
            rows={5}
            className={`${styles.textarea} ${errors.notes ? styles.error : ''}`}
            autoResize
          />
          {errors.notes && <div className={styles.errorMessage}>{errors.notes}</div>}
        </div>

        <div className={styles.buttonGroup}>
          <Button
            label="Cancel"
            icon="pi pi-times"
            className={styles.cancelButton}
            onClick={handleCancel}
            disabled={isSubmitting}
          />
          <Button
            label={isSubmitting ? 'Sending...' : 'Send Request'}
            icon={isSubmitting ? 'pi pi-spin pi-spinner' : 'pi pi-send'}
            className={styles.sendButton}
            onClick={handleSendRequest}
            disabled={isSubmitting}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default RequestModificationModal;

